import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { CheckCircle, AlertTriangle, XCircle, Plane, Calendar } from "lucide-react";

const STATUS_THEMES = {
  green: {
    label: "APTO",
    sub: "Pronto para voar",
    bg: "bg-emerald-500",
    panel: "bg-emerald-500/10 border-emerald-500/40",
    text: "text-emerald-300",
    Icon: CheckCircle,
  },
  amber: {
    label: "ATENÇÃO",
    sub: "Algumas validades estão perto do limite",
    bg: "bg-amber-500",
    panel: "bg-amber-500/10 border-amber-500/40",
    text: "text-amber-300",
    Icon: AlertTriangle,
  },
  red: {
    label: "NÃO APTO",
    sub: "Não pode voar — corrigir as condições abaixo",
    bg: "bg-red-500",
    panel: "bg-red-500/10 border-red-500/40",
    text: "text-red-300",
    Icon: XCircle,
  },
};

const QUAL_LABELS = {
  instr_teorica: "Instrutor Teórica",
  instr_de_instrutores: "Instrutor de Instrutores",
  examinador: "Examinador",
};

const fmtDate = (v) => (v ? String(v).slice(0, 10) : "—");

const hoursToHHmm = (decimal_h) => {
  const v = Number(decimal_h);
  if (!isFinite(v) || v <= 0) return "00:00";
  const totalMin = Math.round(v * 60);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const Crit = ({ ok, label, value, days }) => {
  const tone = ok ? "emerald" : "red";
  return (
    <div
      className={`flex items-center justify-between gap-2 px-3 py-2 rounded border ${
        tone === "emerald"
          ? "border-emerald-500/30 bg-emerald-500/5"
          : "border-red-500/40 bg-red-500/5"
      }`}
    >
      <div className="flex items-center gap-2">
        {ok ? (
          <CheckCircle className="w-4 h-4 text-emerald-400" />
        ) : (
          <XCircle className="w-4 h-4 text-red-400" />
        )}
        <span className="text-sm text-slate-100">{label}</span>
      </div>
      <div className="text-right text-xs text-slate-400">
        {value}
        {typeof days === "number" && (
          <span className={days < 0 ? "ml-2 text-red-400" : days <= 30 ? "ml-2 text-amber-300" : "ml-2 text-slate-500"}>
            ({days >= 0 ? `${days}d` : `expirou há ${-days}d`})
          </span>
        )}
      </div>
    </div>
  );
};

export default function PublicInstructorStatus() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const run = async () => {
      try {
        const r = await axios.get(`${API}/di/public/status/${token}`);
        setData(r.data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Token inválido ou revogado");
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-300 flex items-center justify-center">
        <div className="text-sm">A carregar...</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-300 flex items-center justify-center px-6">
        <div className="max-w-sm text-center space-y-3">
          <XCircle className="w-12 h-12 text-red-400 mx-auto" />
          <h1 className="text-lg font-bold text-red-300">QR inválido</h1>
          <p className="text-sm text-slate-400">{error || "Token não encontrado."}</p>
          <p className="text-xs text-slate-500">
            Pede ao Diretor de Instrução para gerar um novo QR.
          </p>
        </div>
      </div>
    );
  }

  const theme = STATUS_THEMES[data.status] || STATUS_THEMES.red;
  const Icon = theme.Icon;
  const s = data.summary;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="public-status-page">
      <div className="max-w-md mx-auto px-4 py-6 space-y-4">
        {/* Hero status */}
        <div className={`rounded-2xl border ${theme.panel} p-6 text-center`}>
          <Icon className={`w-16 h-16 mx-auto ${theme.text}`} />
          <div className={`mt-3 text-3xl font-black tracking-wider ${theme.text}`} data-testid="public-status-label">
            {theme.label}
          </div>
          <div className="text-sm text-slate-300 mt-1">{theme.sub}</div>
          <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-900/70 border border-slate-700 text-xs">
            <Plane className="w-3.5 h-3.5 text-slate-400" />
            <span className="font-semibold text-slate-100">{data.name}</span>
            <span className="text-slate-500">·</span>
            <span className="font-mono text-slate-400">PU {data.pu_number}</span>
          </div>
          <div className="mt-2 text-[11px] text-slate-500 flex items-center justify-center gap-1">
            <Calendar className="w-3 h-3" /> Avaliado a {data.as_of}
          </div>
        </div>

        {/* 4 criteria */}
        <div className="space-y-2">
          <h2 className="text-xs uppercase tracking-wider text-slate-400 px-1">Critérios</h2>
          <Crit
            ok={s.hours_15h_12m.ok}
            label="≥15:00 voadas / 12 meses"
            value={`${hoursToHHmm(s.hours_15h_12m.value_h)} / ${hoursToHHmm(s.hours_15h_12m.required_h)}`}
          />
          <Crit
            ok={s.medical.ok}
            label="Certificado médico"
            value={fmtDate(s.medical.valid_until)}
            days={s.medical.days_remaining}
          />
          <Crit
            ok={s.pu_license?.ok}
            label="Licença PU (Piloto)"
            value={fmtDate(s.pu_license?.valid_until)}
            days={s.pu_license?.days_remaining}
          />
          <Crit
            ok={s.instructor_license.ok}
            label="Licença instrutor"
            value={fmtDate(s.instructor_license.valid_until)}
            days={s.instructor_license.days_remaining}
          />
        </div>

        {/* Qualificações — uma linha por cada (motivo individual de invalidez) */}
        <div className="space-y-2">
          <h2 className="text-xs uppercase tracking-wider text-slate-400 px-1">Qualificações ANAC</h2>
          {(s.qualifications?.length ?? 0) === 0 ? (
            <div className="px-3 py-2 rounded border border-red-500/30 bg-red-500/10 text-xs text-red-300">
              Sem qualificações registadas.
            </div>
          ) : (
            <div className="space-y-1.5">
              {s.qualifications.map((q, idx) => (
                <Crit
                  key={`${q.type}-${idx}`}
                  ok={q.ok}
                  label={QUAL_LABELS[q.type] || q.type}
                  value={
                    q.valid_until
                      ? `Válida até ${fmtDate(q.valid_until)}`
                      : (q.ok ? "Sem prazo" : "Inválida")
                  }
                  days={q.days_remaining}
                />
              ))}
            </div>
          )}
        </div>

        {/* Uniformizacao */}
        {data.uniformizacao?.status && data.uniformizacao.status !== "never" && (
          <div className="space-y-2">
            <h2 className="text-xs uppercase tracking-wider text-slate-400 px-1">Uniformização anual</h2>
            <Crit
              ok={data.uniformizacao.status === "ok"}
              label="Próxima uniformização"
              value={fmtDate(data.uniformizacao.next_due)}
              days={data.uniformizacao.days_remaining}
            />
          </div>
        )}

        <div className="text-[10px] text-slate-600 text-center pt-2">
          Direção de Instrução
        </div>
      </div>
    </div>
  );
}
